/**
 * Helper utility functions for LiftForge
 */

/**
 * Generate a random 8-character hex string
 */
export const uid = (): string => Math.random().toString(36).slice(2, 10);

/**
 * Array of day names in order: Monday through Sunday
 */
export const DAYS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const;

/**
 * Mapping from full day names to short abbreviations
 */
export const DAY_LABELS: Record<typeof DAYS[number], string> = {
  Monday: 'Mon',
  Tuesday: 'Tue',
  Wednesday: 'Wed',
  Thursday: 'Thu',
  Friday: 'Fri',
  Saturday: 'Sat',
  Sunday: 'Sun',
};

/**
 * Internal map for normalizing various day name formats
 */
const _DAY_MAP: Record<string, typeof DAYS[number]> = {
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
  sun: 'Sunday',
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday',
};

/**
 * Normalize any day name format to canonical full name (Monday-Sunday)
 * Handles short names (Mon, Tue), full names, and various cases
 */
export function normalizeDayName(d: string): typeof DAYS[number] {
  const normalized = _DAY_MAP[(d || '').toLowerCase().slice(0, 3)];
  return normalized || (d as typeof DAYS[number]);
}

/**
 * Convert a Date object to YYYY-MM-DD string format
 */
export function toDateStr(d: Date): string {
  return (
    d.getFullYear() +
    '-' +
    String(d.getMonth() + 1).padStart(2, '0') +
    '-' +
    String(d.getDate()).padStart(2, '0')
  );
}

/**
 * Calculate estimated 1RM using Epley formula
 * Formula: weight × (1 + reps/30)
 * Returns 0 for invalid inputs (weight or reps <= 0)
 */
export function calcE1RM(weight: number, reps: number): number {
  if (weight > 0 && reps > 0) {
    return Math.round(weight * (1 + reps / 30));
  }
  return 0;
}

/**
 * Get the Monday of the week containing the given date.
 */
export function getMonday(d: Date): Date {
  const copy = new Date(d);
  const day = copy.getDay(); // 0=Sun, 1=Mon...
  const diff = day === 0 ? -6 : 1 - day;
  copy.setDate(copy.getDate() + diff);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

/**
 * Calculate which week of a multi-week cycle we are currently in.
 * Returns a 0-indexed week number.
 *
 * @param startDate - ISO date string of the cycle start (a Monday)
 * @param cycleLength - number of weeks in the cycle (2, 3, or 4)
 * @param today - the date to check (defaults to now)
 */
export function getCurrentCycleWeek(
  startDate: string,
  cycleLength: number,
  today?: Date,
): number {
  const now = today || new Date();
  const start = new Date(startDate + 'T00:00:00');
  const currentMonday = getMonday(now);
  const startMonday = getMonday(start);
  const diffMs = currentMonday.getTime() - startMonday.getTime();
  const diffWeeks = Math.floor(diffMs / (7 * 24 * 60 * 60 * 1000));
  // Modulo to cycle, handle negative values (if today is before start)
  return ((diffWeeks % cycleLength) + cycleLength) % cycleLength;
}

/**
 * Get the active weekly schedule for today, considering all schedule modes.
 * Returns the ScheduleSlot array for the current week.
 */
export function getActiveWeekSchedule(state: {
  scheduleType: string;
  weeklySchedule: { day: string; workoutId: string | null }[];
  multiWeekSchedule?: {
    cycleLength: number;
    weeks: { day: string; workoutId: string | null }[][];
    startDate: string;
  };
}): { day: string; workoutId: string | null }[] {
  if (state.scheduleType === 'multiweek' && state.multiWeekSchedule) {
    const mw = state.multiWeekSchedule;
    const weekIdx = getCurrentCycleWeek(mw.startDate, mw.cycleLength);
    return mw.weeks[weekIdx] || state.weeklySchedule;
  }
  return state.weeklySchedule;
}

/**
 * Map legacy effort values (1-10) to intensity levels
 * Used for migration from old effort system to new intensity values
 * 9-10: Maxed Out (0)
 * 7-8: Pushed Hard (1)
 * 4-6: Light Effort (2)
 * 1-3: Warmup (3)
 */
export function effortToIntensity(v: number): number {
  if (v >= 9) return 0; // Maxed Out
  if (v >= 7) return 1; // Pushed Hard
  if (v >= 4) return 2; // Light Effort
  return 3; // Warmup
}
